import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useStatsigClient } from '@statsig/react-bindings';
import { useTranslation } from 'react-i18next';

import ErrorCard from '../../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import GenericCarousel from '../../../../../shared/components/v3/GenericCarousel/GenericCarousel';
import { BDS_PICTOGRAM_PATHS } from '../../../../../shared/constants/bdsPictograms';
import { DOWNLOAD_ICON } from '../../../../../shared/constants/images';
import { CAN_DOWNLOAD_INSURANCE_PDF_FG } from '../../../../../shared/constants/statsig';
import { CLICK_ACTION, DOWNLOAD_POLICY, SCREEN_BENEFITS_DETAIL } from '../../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { PlansContext } from '../../../../../shared/context/plans/plansContext';
import { IInsuranceCoverage } from '../../../../../shared/interfaces/benefits';
import { Company } from '../../../../../shared/services/api/companies/companies';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../../shared/translations/commons';
import { TRANSLATION_MY_PLAN } from '../../../../../shared/translations/plan';
import { toLocaleNumberFormat } from '../../../../../shared/utils/utils';
import './CoveragesCard.scss';

interface ICoveragesCard {
  companyId: string;
  benefitId: string;
  planId: string;
}

const CoveragesCard = ({ companyId, benefitId, planId }: ICoveragesCard) => {
  const { locale } = useContext(CompanyContext);
  const [loadingCoverages, setLoadingCoverages] = useState(false);
  const [coverages, setCoverages] = useState<IInsuranceCoverage[]>([]);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const { client } = useStatsigClient();
  const { selectedBenefit } = useContext(PlansContext);

  const { t } = useTranslation();

  const canSeeCoverages = client.checkGate(CAN_DOWNLOAD_INSURANCE_PDF_FG);
  const isButtonDisabled = !pdfUrl || loadingCoverages;

  const getCoverages = useCallback(async () => {
    setLoadingCoverages(true);
    try {
      const response = await Company.getCompanyInsuranceCoverages(companyId, benefitId, planId, locale);
      setCoverages(response.coverages);
      setPdfUrl(response.pdfUrl);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoadingCoverages(false);
    }
  }, [companyId, benefitId]);

  useEffect(() => {
    getCoverages();
  }, [getCoverages]);

  const getCoverageIcon = (coverage: IInsuranceCoverage): string => {
    if (coverage.icon && BDS_PICTOGRAM_PATHS[coverage.icon]) {
      return BDS_PICTOGRAM_PATHS[coverage.icon];
    }
    return BDS_PICTOGRAM_PATHS.insuranceCoverage;
  };

  const downloadPdf = (url: string) => {
    const gtmParams = {
      ...SCREEN_BENEFITS_DETAIL,
      benefit_sku: selectedBenefit?.sku,
      benefit_id: selectedBenefit?.benefitId,
      provider: selectedBenefit?.providerName
    };
    TagManagerService.pushEvent(CLICK_ACTION, DOWNLOAD_POLICY, gtmParams);
    window.open(url, '_blank');
  };

  if (loadingCoverages) {
    return <Skeleton className="rounded-4xl" height={238} variant="rectangular" />;
  }

  if (error) {
    return (
      <div className="insurance-coverage-error p-10" data-experience="new">
        <ErrorCard
          primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
          secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
          action={getCoverages}
          buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
        />
      </div>
    );
  }

  if (!coverages || coverages.length === 0) {
    return null;
  }

  return (
    <GenericCarousel
      topData={{
        title: t(TRANSLATION_MY_PLAN.cards.insuranceCoverages.title)
      }}
      divider
      buttonData={
        canSeeCoverages
          ? {
              text: t(TRANSLATION_MY_PLAN.cards.insuranceCoverages.button),
              action: () => downloadPdf(pdfUrl ?? ''),
              icon: DOWNLOAD_ICON,
              variant: 'secondary',
              tooltipMessage: isButtonDisabled
                ? t(TRANSLATION_MY_PLAN.cards.insuranceCoverages.buttonTooltip)
                : undefined,
              disabled: isButtonDisabled
            }
          : undefined
      }
      sliders={coverages.map((coverage) => (
        <div key={coverage.id} className="insurance-coverage-item flex flex-col gap-4">
          <div className="coverage-icon">
            <img src={getCoverageIcon(coverage)} alt={coverage.name} className="w-10 h-10" />
          </div>
          <div className="coverage-title">{coverage.name}</div>
          <div className="coverage-description">{coverage.description}</div>
          {canSeeCoverages && (
            <div className="coverage-amount">
              <div className="label">{t(TRANSLATION_MY_PLAN.cards.insuranceCoverages.totalCoverage)}</div>
              <div className="amount">
                {coverage.coveragePercentage
                  ? `${coverage.coveragePercentage}%`
                  : `${coverage.currency} ${toLocaleNumberFormat(coverage.baseAmount)}`}
              </div>
            </div>
          )}
        </div>
      ))}
    />
  );
};

export default CoveragesCard;
